import { type FC, type ReactNode, useState } from 'react'
import { Button } from '../../../components/Button/Button'
import { AccountOpeningCollections, type AccountOpeningCollectionStep, type AccountOpeningPanel } from '../../../models/AccountOpeningDescriptor'
import { AccountCardShell } from './AccountCardShell'
import { AccountRowShell } from './AccountRowShell'
import { formIdPrefix, type ResolvedItem } from '../domain/collections'
import { ConfirmFieldChangeModal } from '../intake/ConfirmFieldChangeModal'
import { EditAccountDetailsModal } from '../intake/EditAccountDetailsModal'
import { type MissingItem } from '../domain/missingFields'
import { PanelHolders } from './PanelHolders'
import { PanelSchemaForm } from './PanelSchemaForm'
import { PanelTypeSummary } from './PanelTypeSummary'
import { useWizardFlow } from '../hooks/WizardFlowContext'
import { type ItemPatch } from '../domain/types'

interface CollectionItemProps {
  step: AccountOpeningCollectionStep
  item: ResolvedItem
  missing: MissingItem[]
  confirmed: boolean
  expanded: boolean
  footer: ReactNode
  onToggleExpand: () => void
  onChange: (patch: ItemPatch) => void
}

export const CollectionItem: FC<CollectionItemProps> = ({ step, item, missing, confirmed, expanded, footer, onToggleExpand, onChange }) => {
  const { readOnly } = useWizardFlow()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showChangeGate, setShowChangeGate] = useState(false)

  const complete = missing.length === 0
  const panels = step.panels ?? []

  const typeSummary = panels.find((panel) => panel.kind === 'typeSummary')
  const editLabel = item.collection === AccountOpeningCollections.ACCOUNTS && !readOnly ? typeSummary?.editLabel : undefined

  const handleEdit = () => {
    if (confirmed) {
      setShowChangeGate(true)
      return
    }
    setShowEditModal(true)
  }

  const renderPanel = (panel: AccountOpeningPanel, index: number): ReactNode => {
    switch (panel.kind) {
      case 'typeSummary':
        return <PanelTypeSummary key={index} item={item} />
      case 'schemaForm':
        return (
          <PanelSchemaForm
            key={index}
            item={item}
            panel={panel}
            missing={missing}
            idPrefix={formIdPrefix(step.id, item, index)}
            onValuesChange={(values) => onChange({ values })}
          />
        )
      case 'holders':
        return <PanelHolders key={index} item={item} panel={panel} onChange={onChange} />
    }
  }

  const editButton = editLabel ? (
    <Button variant='secondary' className='d-flex align-items-center zocks-gap-0.5' onClick={handleEdit}>
      <i className='bi bi-pencil-square' />
      {editLabel}
    </Button>
  ) : null

  const modals =
    item.collection === AccountOpeningCollections.ACCOUNTS ? (
      <>
        <ConfirmFieldChangeModal
          show={showChangeGate}
          onCancel={() => setShowChangeGate(false)}
          onContinue={() => {
            setShowChangeGate(false)
            setShowEditModal(true)
          }}
        />
        {showEditModal ? (
          <EditAccountDetailsModal
            show
            account={item.account}
            onCancel={() => setShowEditModal(false)}
            onUpdate={(updated) => {
              setShowEditModal(false)
              onChange({ selection: updated.selection, typeId: updated.typeId, holders: updated.holders })
            }}
          />
        ) : null}
      </>
    ) : null

  const body = (
    <>
      {panels.map(renderPanel)}
      {footer}
      {modals}
    </>
  )

  if (step.itemLayout === 'plain') {
    return <div className='d-flex flex-column zocks-gap-1'>{body}</div>
  }

  return step.itemLayout === 'card' ? (
    <AccountCardShell heading={item.heading} complete={complete} action={editButton}>
      {body}
    </AccountCardShell>
  ) : (
    <AccountRowShell
      heading={item.heading}
      subheading={item.subheading}
      complete={complete}
      confirmed={confirmed}
      readOnly={readOnly}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
    >
      {body}
    </AccountRowShell>
  )
}
